<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { searchResultsHref, useProductAutocomplete } from '../../shared/use-product-autocomplete.js';

const props = defineProps({
  action: { type: String, default: '/index.php' },
  placeholder: { type: String, default: 'Zoek in meer dan 4000 producten…' },
  initialQuery: { type: String, default: '' },
  autocompleteUrl: {
    type: String,
    default: '/index.php?route=product/search/autocomplete'
  }
});

const query = ref(props.initialQuery);
const rootRef = ref(null);
const inputRef = ref(null);
const activeIndex = ref(-1);

const { items, loading, open, fetchItems } = useProductAutocomplete(query, {
  url: props.autocompleteUrl
});

watch(items, () => {
  activeIndex.value = -1;
});

const term = computed(() => String(query.value || '').trim());
const showPanel = computed(() => open.value && term.value.length > 0);

function highlightParts(label) {
  const text = String(label || '');
  const needle = term.value;
  if (!needle) return [{ text, match: false }];
  const idx = text.toLowerCase().indexOf(needle.toLowerCase());
  if (idx < 0) return [{ text, match: false }];
  return [
    { text: text.slice(0, idx), match: false },
    { text: text.slice(idx, idx + needle.length), match: true },
    { text: text.slice(idx + needle.length), match: false }
  ].filter((part) => part.text);
}

function goTo(item) {
  if (!item || !item.href) return;
  window.location.href = item.href;
}

function goToSearch() {
  if (!term.value) return;
  window.location.href = searchResultsHref(term.value);
}

function onKeydown(e) {
  if (!showPanel.value) return;

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    const max = items.value.length;
    activeIndex.value = activeIndex.value < max ? activeIndex.value + 1 : 0;
    return;
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    const max = items.value.length;
    activeIndex.value = activeIndex.value <= 0 ? max : activeIndex.value - 1;
    return;
  }
  if (e.key === 'Escape') {
    e.preventDefault();
    open.value = false;
    activeIndex.value = -1;
    return;
  }
  if (e.key === 'Enter' && activeIndex.value >= 0) {
    e.preventDefault();
    if (activeIndex.value < items.value.length) {
      goTo(items.value[activeIndex.value]);
    } else {
      goToSearch();
    }
  }
}

function onFocus() {
  if (!term.value) return;
  if (!items.value.length && !loading.value) {
    fetchItems(term.value);
    return;
  }
  open.value = true;
}

function onSubmit(e) {
  if (!term.value) {
    e.preventDefault();
    inputRef.value?.focus();
  }
}

function onDocumentClick(e) {
  if (rootRef.value && !rootRef.value.contains(e.target)) {
    open.value = false;
    activeIndex.value = -1;
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick);
});
</script>

<template>
  <form
    ref="rootRef"
    class="vsb-search"
    :action="action"
    method="get"
    role="search"
    @submit="onSubmit"
  >
    <input type="hidden" name="route" value="product/search" />
    <input
      ref="inputRef"
      v-model="query"
      type="search"
      name="search"
      class="vsb-search__input"
      :placeholder="placeholder"
      autocomplete="off"
      aria-label="Zoek producten"
      aria-autocomplete="list"
      :aria-expanded="showPanel ? 'true' : 'false'"
      @keydown="onKeydown"
      @focus="onFocus"
    />
    <button type="submit" class="vsb-search__btn" aria-label="Zoeken">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-4-4" />
      </svg>
    </button>

    <div v-if="showPanel" class="vsb-search__panel" role="listbox">
      <p v-if="loading && !items.length" class="vsb-search__status">Zoeken…</p>
      <p v-else-if="!loading && !items.length" class="vsb-search__status">
        Geen producten gevonden voor “{{ term }}”
      </p>
      <ul v-else class="vsb-search__list">
        <li
          v-for="(item, index) in items"
          :key="item.product_id || item.label + index"
        >
          <a
            class="vsb-search__item"
            :class="{ 'vsb-search__item--active': index === activeIndex }"
            :href="item.href"
            role="option"
            @mouseenter="activeIndex = index"
            @mousedown.prevent="goTo(item)"
          >
            <img
              v-if="item.thumb"
              class="vsb-search__thumb"
              :src="item.thumb"
              :alt="item.label"
              width="48"
              height="48"
            />
            <span class="vsb-search__item-text">
              <span class="vsb-search__item-name">
                <template v-for="(part, partIndex) in highlightParts(item.label)" :key="partIndex">
                  <mark v-if="part.match">{{ part.text }}</mark>
                  <template v-else>{{ part.text }}</template>
                </template>
              </span>
              <span v-if="item.price" class="vsb-search__item-price">{{ item.price }}</span>
            </span>
          </a>
        </li>
      </ul>
      <button
        type="button"
        class="vsb-search__all"
        :class="{ 'vsb-search__item--active': activeIndex === items.length }"
        @mouseenter="activeIndex = items.length"
        @mousedown.prevent="goToSearch"
      >
        Alle resultaten voor “{{ term }}”
      </button>
    </div>
  </form>
</template>

<style scoped>
.vsb-search {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 640px;
  background: #fff;
  border-radius: 999px;
  padding: 0 0.35rem 0 1.125rem;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.15);
}

.vsb-search__input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.9375rem;
  color: #1e293b;
  min-height: 2.75rem;
  outline: none;
}

.vsb-search__input::placeholder {
  color: #94a3b8;
}

.vsb-search__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--vsb-blue-dark, #0b3d91);
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.2s, background 0.2s;
}

.vsb-search__btn:hover {
  color: var(--vsb-blue-mid, #1456b8);
  background: var(--vsb-blue-subtle, #f7fafe);
}

.vsb-search__panel {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 8px);
  z-index: 10060;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
  overflow: hidden;
  text-align: left;
}

.vsb-search__list {
  list-style: none;
  margin: 0;
  padding: 6px 0;
  max-height: min(60vh, 420px);
  overflow-y: auto;
}

.vsb-search__item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.55rem 0.9rem;
  text-decoration: none;
  color: inherit;
}

.vsb-search__item--active,
.vsb-search__item:hover {
  background: #f1f7fd;
}

.vsb-search__thumb {
  width: 48px;
  height: 48px;
  object-fit: contain;
  background: #f8fafc;
  border: 1px solid #e8eef4;
  border-radius: 8px;
  flex-shrink: 0;
}

.vsb-search__item-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.vsb-search__item-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
}

.vsb-search__item-name mark {
  background: #dbeafe;
  color: inherit;
  font-weight: 700;
  padding: 0;
}

.vsb-search__item-price {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--vsb-blue-dark, #0b3d91);
}

.vsb-search__status {
  margin: 0;
  padding: 0.9rem 1rem;
  font-size: 0.875rem;
  color: #64748b;
}

.vsb-search__all {
  display: block;
  width: 100%;
  border: none;
  border-top: 1px solid #e8eef4;
  background: #f8fafc;
  padding: 0.75rem 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--vsb-blue-mid, #1456b8);
  cursor: pointer;
  text-align: left;
}

.vsb-search__all:hover,
.vsb-search__all.vsb-search__item--active {
  background: #eef4fb;
}
</style>
